import classNames from 'classnames';
import { FC, RefAttributes, useMemo } from 'react';
import { Link as ReactLink, LinkProps } from 'react-router';

import { trackExternalLinkClick } from '@/loader/trackExternalLinkClick';

import './Link.scss';

interface LinksProps extends LinkProps, RefAttributes<HTMLAnchorElement> {
  className?: string;
  isExternal?: boolean;
  defaultCssStyles?: boolean;
}

export const Link: FC<LinksProps> = ({
  className,
  to,
  isExternal = false,
  defaultCssStyles = false,
  children,
  preventScrollReset,
  ...restProps
}) => {
  const isInternalLink = useMemo((): boolean => {
    if (typeof to !== 'string') {
      return !isExternal;
    }

    try {
      const url = new URL(to, window.location.origin);
      return url.origin === window.location.origin && !isExternal;
    } catch {
      return !isExternal;
    }
  }, [to, isExternal]);

  const styles = classNames({ 'custom-link': !defaultCssStyles }, className);

  if (isInternalLink) {
    return (
      <ReactLink data-testid="link" to={to} className={styles} preventScrollReset={preventScrollReset} {...restProps}>
        {children}
      </ReactLink>
    );
  }

  const onExternalClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    window.open(to.toString(), '_blank');
    !isInternalLink && trackExternalLinkClick(to as string);
    restProps.onClick?.(e);
  };

  return (
    <a
      data-testid="link"
      href={to as string}
      onClick={onExternalClick}
      target={!isInternalLink ? '_blank' : '_self'}
      className={styles}
      rel="noopener noreferrer"
      {...restProps}
    >
      {children}
    </a>
  );
};
